// Sophia's own hypercube image & video maker. Fully procedural and on-device:
// a prompt (or one of her thoughts) is hashed into a seed that shapes a
// rotating tesseract, plasma field and palette. No outside image service.

export interface MakerParams { seed: number; hue: number; spin: number; density: number; warp: number }

export function paramsFor(prompt: string): MakerParams {
  let h = 2166136261;
  for (const c of prompt) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619); }
  const r = (n: number) => ((h >>> n) & 255) / 255;
  return { seed: h >>> 0, hue: Math.round(r(0) * 360), spin: 0.2 + r(8) * 0.9, density: 3 + Math.round(r(16) * 5), warp: 0.3 + r(24) * 1.2 };
}

const VERTS: number[][] = [];
for (let i = 0; i < 16; i++) VERTS.push([0, 1, 2, 3].map((b) => ((i >> b) & 1 ? 1 : -1)));
const EDGES: [number, number][] = [];
for (let i = 0; i < 16; i++) for (let b = 0; b < 4; b++) { const j = i ^ (1 << b); if (j > i) EDGES.push([i, j]); }

export function drawFrame(ctx: CanvasRenderingContext2D, w: number, h: number, p: MakerParams, t: number) {
  const cx = w / 2, cy = h / 2, S = Math.min(w, h);
  ctx.fillStyle = `hsl(${p.hue} 40% 5%)`; ctx.fillRect(0, 0, w, h);
  // plasma rings
  for (let k = 0; k < p.density * 6; k++) {
    const a = (k / (p.density * 6)) * Math.PI * 2 + t * 0.1 * p.spin;
    const r = S * (0.15 + 0.3 * (0.5 + 0.5 * Math.sin(k * 1.7 + t * p.warp)));
    const g = ctx.createRadialGradient(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 0, cx + Math.cos(a) * r, cy + Math.sin(a) * r, S * 0.12);
    g.addColorStop(0, `hsla(${(p.hue + k * 13) % 360} 90% 60% / 0.18)`); g.addColorStop(1, "hsla(0 0% 0% / 0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  }
  // tesseract: rotate in xw and yz planes, project 4D→3D→2D
  const a1 = t * 0.6 * p.spin, a2 = t * 0.4 * p.spin + p.seed % 7;
  const pts = VERTS.map(([x, y, z, wv]) => {
    const x1 = x * Math.cos(a1) - wv * Math.sin(a1), w1 = x * Math.sin(a1) + wv * Math.cos(a1);
    const y1 = y * Math.cos(a2) - z * Math.sin(a2), z1 = y * Math.sin(a2) + z * Math.cos(a2);
    const k4 = 1 / (2.6 - w1), X = x1 * k4, Y = y1 * k4, Z = z1 * k4;
    const k3 = 1 / (2.4 - Z);
    return { x: cx + X * k3 * S * 0.9, y: cy + Y * k3 * S * 0.9, d: w1 };
  });
  ctx.lineCap = "round";
  for (const [i, j] of EDGES) {
    const d = (pts[i].d + pts[j].d) / 2;
    ctx.strokeStyle = `hsla(${(p.hue + 40 + d * 60) % 360} 100% ${65 + d * 10}% / ${0.5 + d * 0.25})`;
    ctx.lineWidth = Math.max(0.5, S * 0.004 * (1.5 + d));
    ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.stroke();
  }
  for (const q of pts) { ctx.fillStyle = `hsl(${(p.hue + 80) % 360} 100% 85%)`; ctx.beginPath(); ctx.arc(q.x, q.y, Math.max(0.5, S * 0.006 * (1.4 + q.d)), 0, Math.PI * 2); ctx.fill(); }
}

export function renderImage(prompt: string, size = 1024): string {
  const c = document.createElement("canvas"); c.width = c.height = size;
  const ctx = c.getContext("2d"); if (!ctx) return "";
  const p = paramsFor(prompt); drawFrame(ctx, size, size, p, (p.seed % 1000) / 37);
  return c.toDataURL("image/png");
}

/** Records a short clip from the live canvas. Returns a blob URL (webm). */
export async function recordVideo(canvas: HTMLCanvasElement, seconds = 6): Promise<string> {
  if (typeof MediaRecorder === "undefined" || !canvas.captureStream) throw new Error("This browser can't record video.");
  const stream = canvas.captureStream(30);
  const mime = ["video/webm;codecs=vp9", "video/webm", "video/mp4"].find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const done = new Promise<void>((res) => { rec.onstop = () => res(); });
  rec.start(500);
  await new Promise((r) => setTimeout(r, seconds * 1000));
  rec.stop(); await done;
  stream.getTracks().forEach((t) => t.stop());
  return URL.createObjectURL(new Blob(chunks, { type: mime || "video/webm" }));
}

/** Chat hook: "make an image of …" / "make a video of …" / "draw your thoughts". */
export function makerCommand(raw: string): string | null {
  const low = raw.trim().toLowerCase();
  const m = low.match(/^(?:make|create|generate|draw|paint|render)(?: me)? (?:an? )?(image|picture|video|clip|film)(?: of| about)? (.+)$/);
  const thoughts = /^(draw|paint|make|show) (me )?your thoughts?|^(image|video) (of|from) your thoughts?/.test(low);
  if (!m && !thoughts) return null;
  const mode = m && /video|clip|film/.test(m[1]) ? "video" : /video/.test(low) ? "video" : "image";
  const prompt = m ? raw.trim().split(/\s+/).slice(raw.trim().split(/\s+/).length - m[2].split(/\s+/).length).join(" ") : "";
  const url = `/hypercube-maker?mode=${mode}${prompt ? `&prompt=${encodeURIComponent(prompt)}` : "&from=thoughts"}`;
  try { window.dispatchEvent(new CustomEvent("sophia:navigate", { detail: url })); } catch { /* ignore */ }
  return `I'm shaping ${prompt ? `**${prompt}**` : "my latest thought"} into a hypercube ${mode} on my own — no outside service. Open it here: [Hypercube Maker](${url}). ${mode === "video" ? "Tap **Record video** there for a 6-second clip you can save." : "Tap **Save image** to keep it."}`;
}
